import {ref} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";

export interface BaseCalendarConfig {
    min?: Date;
    max?: Date;
    disabled?: boolean;
    locale?: string;
    startDay?: "Monday" | "Sunday";
    disabledDates?: Array<Date>;
    enabledDates?: Array<Date>;
    disabledWeekdays?: Array<number>;
    isDateDisabled?: (date: Date) => boolean;
}

// Note: CalendarDay interface is defined in each specific calendar implementation
// (DatePicker and DateRangePicker) as they have different requirements

export abstract class BaseCalendar<TEvents> {
    // Reactive state
    _disabled: boolean;
    _viewMonth: number;
    _viewYear: number;

    // Non-reactive config
    protected readonly min?: Date;
    protected readonly max?: Date;
    protected readonly locale: string;
    protected readonly startDay: "Monday" | "Sunday";
    protected readonly disabledDates: Array<Date>;
    protected readonly enabledDates?: Array<Date>;
    protected readonly disabledWeekdays: Array<number>;
    protected readonly isDateDisabledFn?: (date: Date) => boolean;
    public readonly events: EventEmitter<TEvents>;

    // Cached format functions (much faster than toLocaleDateString)
    // Using bound functions to avoid Valtio proxy issues with Intl.DateTimeFormat
    protected readonly formatMonth: (date: Date) => string;
    protected readonly formatWeekday: (date: Date) => string;
    protected readonly formatMonthYear: (date: Date) => string;

    protected constructor(config: BaseCalendarConfig = {}) {
        const now = new Date();
        this._disabled = config.disabled ?? false;
        this._viewMonth = now.getMonth();
        this._viewYear = now.getFullYear();

        this.min = config.min;
        this.max = config.max;
        this.locale = config.locale ?? "en";
        this.startDay = config.startDay ?? "Sunday";
        this.disabledDates = config.disabledDates ?? [];
        this.enabledDates = config.enabledDates;
        this.disabledWeekdays = config.disabledWeekdays ?? [];
        this.isDateDisabledFn = config.isDateDisabled;

        this.events = ref(new EventEmitter<TEvents>()) as EventEmitter<TEvents>;

        // Initialize cached formatters (created once, reused for all formatting)
        // Store bound format functions to avoid Valtio proxy issues
        const monthFormatter = new Intl.DateTimeFormat(this.locale, {month: "long"});
        const weekdayFormatter = new Intl.DateTimeFormat(this.locale, {weekday: "short"});
        const monthYearFormatter = new Intl.DateTimeFormat(this.locale, {month: "long", year: "numeric"});

        this.formatMonth = monthFormatter.format.bind(monthFormatter);
        this.formatWeekday = weekdayFormatter.format.bind(weekdayFormatter);
        this.formatMonthYear = monthYearFormatter.format.bind(monthYearFormatter);
    }

    // Computed getters
    get disabled(): boolean {
        return this._disabled;
    }

    /**
     * True when the current view is already at (or before) the min date's month,
     * so the "previous month / previous year" navigation should be hidden.
     */
    get isAtMinMonth(): boolean {
        if (!this.min) return false;
        const minYear = this.min.getFullYear();
        const minMonth = this.min.getMonth();
        return this._viewYear < minYear || (this._viewYear === minYear && this._viewMonth <= minMonth);
    }

    /**
     * True when the current view year is already the min year or earlier,
     * so the "previous year" navigation should be disabled.
     */
    get isAtMinYear(): boolean {
        if (!this.min) return false;
        return this._viewYear <= this.min.getFullYear();
    }

    get viewMonth(): number {
        return this._viewMonth;
    }

    get viewYear(): number {
        return this._viewYear;
    }

    get monthName(): string {
        return this.formatMonth(new Date(this._viewYear, this._viewMonth));
    }

    get weekdays(): Array<string> {
        // Return translation keys for weekdays
        // Keys match the format used in calendar namespace: "mon", "tue", "wed", etc.
        if (this.startDay === "Sunday") {
            return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
        }
        // Monday first (default)
        return ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    }

    protected formatDateToMMDDYYYY(date: Date): string {
        return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });
    }

    protected parseDateFromMMDDYYYY(dateString: string): Date | null {
        const [month, day, year] = dateString.split("/").map(Number);

        if ([month, day, year].some((n) => isNaN(n))) return null;

        const date = new Date(year, month - 1, day);
        return isNaN(date.getTime()) ? null : date;
    }

    /**
     * Parse date from MM-DD-YYYY, YYYY-MM-DD, or MM/DD/YYYY format
     */
    protected parseDate(dateString: string): Date | null {
        if (dateString.includes("-")) {
            const parts = dateString.split("-");
            // If first part is 4 digits, it's YYYY-MM-DD format
            if (parts[0].length === 4) {
                const [year, month, day] = parts.map(Number);
                if (![year, month, day].some((n) => isNaN(n))) {
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) return date;
                }
            } else {
                // MM-DD-YYYY format
                const [month, day, year] = parts.map(Number);
                if (![month, day, year].some((n) => isNaN(n))) {
                    const date = new Date(year, month - 1, day);
                    if (!isNaN(date.getTime())) return date;
                }
            }
        }

        // Fallback to MM/DD/YYYY format
        return this.parseDateFromMMDDYYYY(dateString);
    }
    // Validation helpers
    protected isDateInRange(date: Date): boolean {
        // If enabledDates is set, only those dates are allowed
        if (this.enabledDates && this.enabledDates.length > 0) {
            return this.enabledDates.some((enabledDate) => this.isSameDay(date, enabledDate));
        }

        // Check min/max range
        if (this.min && this.isBefore(date, this.min)) return false;
        if (this.max && this.isAfter(date, this.max)) return false;

        // Check if date is in disabledDates list
        if (this.disabledDates.some((disabledDate) => this.isSameDay(date, disabledDate))) {
            return false;
        }

        // Check if weekday is disabled
        if (this.disabledWeekdays.includes(date.getDay())) {
            return false;
        }

        // Check custom disabled callback
        return !(this.isDateDisabledFn && this.isDateDisabledFn(date));
    }

    protected isSameDay(date1: Date, date2: Date): boolean {
        return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    }

    /**
     * True when date is today. If viewMonth and viewYear are passed, true only when
     * date is today AND in that month (so "today" is highlighted only in the current month).
     */
    protected isToday(date: Date, viewMonth?: number, viewYear?: number): boolean {
        if (!this.isSameDay(date, new Date())) return false;
        if (viewMonth === undefined || viewYear === undefined) return true;
        return date.getMonth() === viewMonth && date.getFullYear() === viewYear;
    }

    protected isBefore(date1: Date, date2: Date): boolean {
        const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
        return d1.getTime() < d2.getTime();
    }

    protected isAfter(date1: Date, date2: Date): boolean {
        const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
        const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
        return d1.getTime() > d2.getTime();
    }

    protected formatDate(date: Date): string {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    }

    // Calendar generation
    protected getDaysInMonth(month: number, year: number): Array<Date> {
        const daysCount = new Date(year, month + 1, 0).getDate();
        const days: Array<Date> = [];

        for (let day = 1; day <= daysCount; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    }

    protected getPreviousMonthDays(firstDayOfMonth: Date): Array<Date> {
        const dayOfWeek = firstDayOfMonth.getDay();
        const startOffset = this.startDay === "Monday" ? (dayOfWeek === 0 ? 6 : dayOfWeek - 1) : dayOfWeek;

        if (startOffset === 0) return [];

        const month = firstDayOfMonth.getMonth();
        const year = firstDayOfMonth.getFullYear();
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevYear = month === 0 ? year - 1 : year;
        const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

        const days: Array<Date> = [];
        for (let i = startOffset - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            days.push(new Date(prevYear, prevMonth, day));
        }

        return days;
    }

    protected getNextMonthDays(totalDays: number): Array<Date> {
        const remainingDays = 42 - totalDays;
        if (remainingDays <= 0) return [];

        const nextMonth = this._viewMonth === 11 ? 0 : this._viewMonth + 1;
        const nextYear = this._viewMonth === 11 ? this._viewYear + 1 : this._viewYear;

        const days: Array<Date> = [];
        for (let day = 1; day <= remainingDays; day++) {
            days.push(new Date(nextYear, nextMonth, day));
        }

        return days;
    }

    // Navigation
    nextMonth(): void {
        if (this._viewMonth === 11) {
            this._viewMonth = 0;
            this._viewYear += 1;
        } else {
            this._viewMonth += 1;
        }
    }

    previousMonth(): void {
        if (this.isAtMinMonth) return;
        if (this._viewMonth === 0) {
            this._viewMonth = 11;
            this._viewYear -= 1;
        } else {
            this._viewMonth -= 1;
        }
    }

    nextYear(): void {
        this._viewYear += 1;
    }

    previousYear(): void {
        if (this.min && this._viewYear <= this.min.getFullYear()) return;
        this._viewYear -= 1;
    }

    goToToday(): void {
        const today = new Date();
        this._viewMonth = today.getMonth();
        this._viewYear = today.getFullYear();
    }

    setViewMonth(month: number, year: number): void {
        this._viewMonth = month;
        this._viewYear = year;
    }

    // State management
    setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    enable(): void {
        this._disabled = false;
    }

    disable(): void {
        this._disabled = true;
    }

    // Abstract methods to be implemented by subclasses
    abstract get calendarDays(): Array<unknown>;
}
